import { useEffect, useState } from 'react'
import { Button, Table } from 'react-bootstrap'
import { FaEdit } from 'react-icons/fa'
import useFetch from '../../../_fetch'
import ScheduledReportModal from './scheduled-report-modal'
import DownloadReportModal from './download-report-modal'
import { IFilterSelectorOpts_Selected, PublisherOrCampaign } from '../home/dashboard-content/types'
import { ICustomAlert } from '../alerts/custom-alerts/custom-alerts'

export type IScheduledReport = {
    id: string
    name: string
    type: 'daily' | 'weekly' | 'monthly'
    emails: string[] 
    campaigns: PublisherOrCampaign[] | string[] | true
    publishers: PublisherOrCampaign[] | true
    channels: PublisherOrCampaign[] | true
    networks: string[] | true
    countries: string[] | true
    includePerformanceData?: boolean
    includeMonthlyPerformance?: boolean
    includeKeyMetrics?: boolean
    includeAlerts?: boolean
    alertIds?: string[]
    useCustomEmail?: boolean
    customEmailSubject?: string
    customEmailBody?: string
}

function generateRandomString() {
    return Math.random().toString(36).slice(2)
}

const ScheduledReports = () => {
    const { doFetch } = useFetch()
    const [reports, setReports] = useState<IScheduledReport[]>([])
    const [alerts, setAlerts] = useState<ICustomAlert[]>([])
    const [campaigns, setCampaigns] = useState<PublisherOrCampaign[]>([])
    const [publishers, setPublishers] = useState<PublisherOrCampaign[]>([])
    const [networks, setNetworks] = useState<string[]>([])
    const [countries, setCountries] = useState<string[]>([])
    const [channels, setChannels] = useState<PublisherOrCampaign[]>([])

    const [showModal, setShowModal] = useState(false)
    const [currentReport, setCurrentReport] = useState<IScheduledReport | null>(null)

    const [showDownloadModal, setShowDownloadModal] = useState(false)

    useEffect(() => {
        (async () => {
            const res = await doFetch('/data/relation-info')
            if (res.status === 200) {
                const data = await res.json()
                setCampaigns(data.campaigns)
                setPublishers(data.publishers)
                setNetworks(data.networks.map((f:any) => f.network))
                setCountries(data.countries.map((f:any) => f.country))
                setChannels(data.channels)
            }
        })()
    }, [])

    useEffect(() => {
        (async () => {
            const res = await doFetch('/scheduled-reports/get-all')
            if (res.status === 200) {
                const data = await res.json()
                setReports(data.reports)
            }
            const res2 = await doFetch('/alerts/get-custom-alerts')
            if(res2.status === 200) {
                const data = await res2.json()
                setAlerts(data)
            }
        })()
    }, [])

    const handleOpenModal = (report: IScheduledReport | null = null) => {
        setCurrentReport(report)
        setShowModal(true)
    }

    const handleCloseModal = () => {
        setShowModal(false)
        setCurrentReport(null)
    }

    const handleSave = async (newReport: IScheduledReport) => {
        if (newReport.id) {
            const res = await doFetch(`/scheduled-reports/update`, { report: newReport })
            if (res.status === 200) {
                await res.json()
                setReports(reports.map(report => report.id === newReport.id ? newReport : report))
            } else {
                alert('Failed to update the report.')
            }
        } else {
            const createdReport = { ...newReport, id: generateRandomString() }
            const res = await doFetch('/scheduled-reports/create', { report: createdReport })
            if (res.status === 200) {
                setReports([...reports, createdReport])
            } else {
                alert('Failed to create the report.')
            }
        }
    }

    const handleDeleteReport = async (reportId: string) => {
        const res = await doFetch(`/scheduled-reports/delete/${reportId}`)
        if (res.status === 200) {
            setReports(prevReports => prevReports.filter(report => report.id !== reportId))
        }
    }
    const handleOpenDownloadModal = () => {
        setShowDownloadModal(true)
    }

    const handleCloseDownloadModal = () => {
        setShowDownloadModal(false)
    }

    const handleDownloadReport = async (fromDate: string, toDate: string, filters: IFilterSelectorOpts_Selected, reportTitle: string, reportType: string, selectedAlertIds?: string[], includeMonthlyPerformance?: boolean, includeKeyMetrics?: boolean) => {
        console.log('Downloading report for:', { fromDate, toDate, filters, reportTitle, reportType, selectedAlertIds, includeMonthlyPerformance, includeKeyMetrics })

        const res = await doFetch('/reports/download-report', {
            reportOptions: { fromDate, toDate, reportName: reportTitle, reportType, selectedAlertIds, includeMonthlyPerformance, includeKeyMetrics, ...filters }
        })
        const pdfData = await res.arrayBuffer()
    
        const pdfBlob = new Blob([pdfData], { type: 'application/pdf' })
    
        const link = document.createElement('a')
        const url = URL.createObjectURL(pdfBlob)
        link.href = url
        link.download = `${reportTitle || 'report'}.pdf`
        link.click()
    
        
        URL.revokeObjectURL(url)
    
        setShowDownloadModal(false)
    }
    

    return (
        <div>
            <div>
                <p className="mb-1 text-muted">
                    <strong>Note:</strong> When you add a scheduled report, it will automatically be sent via email according to the selected frequency (daily, weekly, or monthly).
                </p>
                <div className='d-flex'>
                    <Button variant="primary" className="mt-2 mb-3" onClick={() => handleOpenModal()}>
                        Add Scheduled Report
                    </Button>
                    <Button variant='success' className='mt-2 mb-3 ms-2' onClick={handleOpenDownloadModal}>
                        Download report
                    </Button>
                </div>
            </div>

            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>Report title</th>
                        <th>Frequency</th>
                        <th>Recipients</th>
                    </tr>
                </thead>
                <tbody>
                    {reports.map((report) => (
                        <tr key={report.id}>
                            <td>
                                <a href="#" onClick={(e) => { e.preventDefault(); handleOpenModal(report); }}>
                                    <FaEdit className="me-2" />
                                </a>
                                {report.name}
                            </td>
                            <td>{report.type.charAt(0).toUpperCase() + report.type.slice(1)}</td>
                            <td>{report.emails.join(', ')}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <ScheduledReportModal
                show={showModal}
                campaigns={campaigns}
                onClose={handleCloseModal}
                onSave={handleSave}
                onDelete={handleDeleteReport}
                report={currentReport || undefined}
                countries={countries}
                networks={networks}
                publishers={publishers}
                channels={channels}
                alerts={alerts}
            />

            <DownloadReportModal
                show={showDownloadModal}
                onClose={handleCloseDownloadModal}
                onDownload={handleDownloadReport}
                campaigns={campaigns}
                countries={countries}
                networks={networks}
                publishers={publishers}
                alerts={alerts}
                channels={channels}
            />
        </div>
    )
}

export default ScheduledReports
